import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { debounce, get, isNil, pickBy, some } from 'lodash';
import { useMemo, useState } from 'react';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  FORMAT_DATE_TIME,
  LIMIT_GET_LIST_AUTO_COMPLETE,
  LIMIT_GET_LIST_BILLS_OF_LADING,
} from 'src/utils/constants/config';

import { useQueryGetTradingCompanies, useQueryListTradeDataEntityDetail } from 'src/services/queries/useQueryTradeData';
import { TypeCompanyTradeDataFormSearch } from 'src/utils/typings/dto/tradeData';
import * as Yup from 'yup';

import { ArrowForward as ArrowForwardIcon, TravelExplore } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import CreditCostIndicator from 'src/components/common/CreditCostIndicator';
import FormItemCountry from 'src/components/Form/FormItemCountry';
import { CreditCoinIcon } from 'src/components/Icons';
import CheckedCredit from 'src/components/Icons/CheckedCredit';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useMutateCheckExistCTD } from 'src/services/mutate/useMutateTradeDataWithoutHSCode';
import {
  useQueryCTDTransactionsCount,
  useQueryCTDTransactionsPaymentStatus,
} from 'src/services/queries/useQueryCompanyTradeData';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import { TypeBuyerSupplier, TypeCompanyTradeDataSearch } from 'src/utils/constants/enum';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { getTradeDataEndMonth } from 'src/utils/helpers/dayjs';
import { formatNumberWithSuffix } from 'src/utils/helpers/number';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { getFormItemError, renderFormItemError } from 'src/utils/helpers/renderFormItemError';
import { checkCountryHaveState, getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';
import { FeatureAction } from 'src/utils/constants/featuresPoint';

interface IProps {
  handleClose: () => void;
}

const FormSearchCompanyTrade = (props: IProps) => {
  const { handleClose } = props;
  const theme = useTheme();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const { mutateAsync, isPending } = useMutateCheckExistCTD();
  const [initTradeFrom, initTradeTo] = useMemo(() => {
    return [
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD'),
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD'),
    ];
  }, [runtimeConfig.DEFAULT_TRADE_DATA_FROM, runtimeConfig.DEFAULT_TRADE_DATA_TO]);
  const queryParams = useQueryParams();

  const { t } = useTranslation();

  const getDefaultValue = (key: string) => {
    return get(queryParams, key, '') as string;
  };

  const navigate = useNavigate();

  const initCompanyResp = useQueryListTradeDataEntityDetail(queryParams.company_id);

  const defaultJurisdiction = useMemo(() => {
    if (!queryParams.jurisdiction) return undefined;
    const country = getCountryByCountryCode(queryParams.country_code);

    return {
      country_code: queryParams.country_code,
      jurisdiction: queryParams.jurisdiction,
      optionLabel: checkCountryHaveState(queryParams.country_code)
        ? `${country.label} - ${queryParams.jurisdiction}`
        : queryParams.jurisdiction,
    };
  }, [queryParams.jurisdiction, queryParams.country_code]);

  const companyDefaultValue = useMemo(
    () => ({
      id: initCompanyResp.data?.data?.id || '',
      country_code: initCompanyResp.data?.data?.country_code || '',
      name: initCompanyResp.data?.data?.name || '',
      jurisdiction: initCompanyResp?.data.data?.jurisdiction || '',
      description: initCompanyResp?.data.data?.jurisdiction,
    }),
    [],
  );

  const formik = useFormik<TypeCompanyTradeDataFormSearch>({
    enableReinitialize: true,
    initialValues: {
      company: companyDefaultValue,
      jurisdiction: defaultJurisdiction,
      trade_from: getDefaultValue('start_date') ? dayjs(getDefaultValue('start_date'), 'YYYYMMDD') : initTradeFrom,
      trade_to: getDefaultValue('end_date') ? dayjs(getDefaultValue('end_date'), 'YYYYMMDD') : initTradeTo,
      country_code: getDefaultValue('country_code'),
      keyword: getDefaultValue('keyword'),
      view_as:
        queryParams.company_type === TypeCompanyTradeDataSearch.BOTH
          ? TypeCompanyTradeDataSearch.BOTH
          : getDefaultValue('is_buyer') === 'true'
            ? TypeBuyerSupplier.buyer
            : TypeBuyerSupplier.supplier,
    },
    initialTouched: {
      trade_from: true,
      trade_to: true,
    },
    validationSchema: Yup.object().shape({
      // company: Yup.mixed().required('Company is required'),
      // jurisdiction: Yup.mixed().required('Jurisdiction is required'),
      keyword: Yup.string().test('minimum-characters', 'Please enter at least 3 characters', function (value) {
        if (!value) return true;
        return value.length >= 3;
      }),
      trade_from: Yup.mixed().test('is-greater', 'Invalid date range', function (_, context) {
        const trade_from = dayjs(context.parent.trade_from || 'Invalid date');
        const trade_to = dayjs(context.parent.trade_to || 'Invalid date');
        if (!(trade_from.isValid() && trade_to.isValid())) return true;
        return trade_from.isBefore(trade_to);
      }),
      trade_to: Yup.mixed(),
      export_country: Yup.mixed()
        .nullable()
        .test('not-duplicated', 'Export and import countries must be different', (_, context) => {
          const export_country = context.parent.export_country?.code;
          const import_country = context.parent.import_country?.code;

          if (!(export_country && import_country)) return true;

          return export_country !== import_country;
        }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!shouldExistAtLeastKeywordOrCompany) return;

      setSubmitting(false);

      // const checkExistCTDRes = await mutateAsync(bolRequestPayload);
      const tab = bolRequestPayload.is_buyer ? TypeTabTradeData.Suppliers : TypeTabTradeData.Buyers;

      const searchString = handleQueryParamFromObject(bolRequestPayload) + `&tab=${tab}`;

      navigate({
        pathname: AuthRouteKeys.CompanyTradeData,
        search: searchString,
      });
      handleClose();
    },
  });

  const isCountryHaveState = checkCountryHaveState(formik.values.jurisdiction?.country_code);
  const [supplierSearchKeyword, setSupplierSearchKeyword] = useState('');

  const shouldExistAtLeastKeywordOrCompany = useMemo(() => {
    return !!formik.values.keyword || !!formik.values.company?.id;
  }, [formik.values.keyword, formik.values.company?.id]);

  const bolRequestPayload = useMemo(() => {
    const values = formik.values;
    return pickBy(
      {
        start_date: values.trade_from?.startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
        end_date: getTradeDataEndMonth(values?.trade_to).format(FORMAT_DATE_TIME.yyyy_mm_dd),
        offset: 0,
        limit: LIMIT_GET_LIST_BILLS_OF_LADING,
        company_id: formik.values.company?.id,
        jurisdiction: formik.values.jurisdiction?.jurisdiction,
        country_code: formik.values?.country_code,
        keyword: values.keyword,
        is_buyer: values.view_as === TypeBuyerSupplier.buyer,
        company_type: values.view_as === TypeCompanyTradeDataSearch.BOTH ? TypeCompanyTradeDataSearch.BOTH : null,
      },
      (value) => !isNil(value) && value !== '',
    );
  }, [formik.values]);

  const transactionsPaymentStatusResp = useQueryCTDTransactionsPaymentStatus(bolRequestPayload);
  const transactionsCountResp = useQueryCTDTransactionsCount(bolRequestPayload);

  const isTransactionQueryPaid = transactionsPaymentStatusResp.data?.data?.payment_status;

  const { getFeaturePoints, hasAccess } = useFeatureAccess();
  const isAccessBuyer = hasAccess([URL_ENDPOINT.tradeData.buyersTable]);
  const tradeDataEndpoint = isAccessBuyer
    ? URL_ENDPOINT.tradeData.buyersTable
    : URL_ENDPOINT.tradeData.suppliersTable;
  const pointsViewTradeData = getFeaturePoints(tradeDataEndpoint, FeatureAction.VIEW);

  // useEffect(() => {
  //   if (!formik.values.company?.id) return;
  //   mutateAsync({
  //     entityId: formik.values.company?.id,
  //     type: TypeCheckEntityPaid.COMPANY,
  //   }).then((response) => {
  //     setIsTransactionQueryPaid(response?.check_entity_id_type_created_30_days_result || false);
  //   });
  // }, [formik.values.company?.id]);

  const dataCompanyResp = useQueryGetTradingCompanies({
    limit: LIMIT_GET_LIST_AUTO_COMPLETE,
    offset: '0',
    keyword: supplierSearchKeyword,
    jurisdiction: formik.values.jurisdiction?.jurisdiction,
    company_id: formik.values.company?.id,
    start_date: formik.values.trade_from?.startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
    end_date: getTradeDataEndMonth(formik.values?.trade_to).format(FORMAT_DATE_TIME.yyyy_mm_dd),
  });

  const renderError = (id: string) => {
    return (
      get(formik.touched, id) &&
      get(formik.errors, id) && (
        <FormHelperText sx={{ marginLeft: 2 }} error id="standard-weight-helper-text-email-login">
          {get(formik.errors, id, '').toString()}
        </FormHelperText>
      )
    );
  };

  return (
    <Box>
      <FormikForm formik={formik}>
        <Box width={600}>
          <Typography mb={4} textAlign={'center'} color={theme.palette.text.secondary} variant="body1">
            {t(
              'Search for data records by company name. Gain a 360-degree view of a company’s import and export data.',
            )}
          </Typography>

          <Stack direction={'column'} spacing={2}>
            <FormControl fullWidth>
              <FormLabel id="form-search-company-trade-data-view-as">
                <Typography variant="caption2">View As</Typography>
              </FormLabel>

              <RadioGroup
                value={formik.values.view_as}
                onChange={formik.handleChange}
                row
                id="view_as"
                aria-labelledby="form-search-company-trade-data-view-as"
                name="view_as"
              >
                <FormControlLabel
                  slotProps={{
                    typography: {
                      variant: 'caption2',
                    },
                  }}
                  value={TypeBuyerSupplier.buyer}
                  control={<Radio size="small" />}
                  label="Buyer"
                />
                <FormControlLabel
                  slotProps={{
                    typography: {
                      variant: 'caption2',
                    },
                  }}
                  value={TypeBuyerSupplier.supplier}
                  control={<Radio size="small" />}
                  label="Supplier"
                />
                <FormControlLabel
                  slotProps={{
                    typography: {
                      variant: 'caption2',
                    },
                  }}
                  value={TypeCompanyTradeDataSearch.BOTH}
                  control={<Radio size="small" />}
                  label="Both Roles"
                />
              </RadioGroup>
            </FormControl>
            <Box>
              <TextField
                defaultValue={formik.values.keyword}
                label="Keyword (Company Name)"
                fullWidth
                onChange={debounce(formik.handleChange, 500)}
                onBlur={formik.handleBlur}
                required
                size="small"
                id="keyword"
                name="keyword"
                placeholder="Company Name"
              />
              {renderFormItemError(getFormItemError('keyword', formik))}
            </Box>
            {/*<Stack direction={'row'} gap={2}>*/}

            {/* <FormGroup>
                <FormControlLabel
                  control={
                    <ButtonGroup fullWidth variant="outlined">
                      <Button
                        size="small"
                        onClick={() => setIsBuyer(true)}
                        variant={isBuyer ? 'contained' : 'outlined'}
                      >
                        Buyer
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setIsBuyer(false)}
                        variant={!isBuyer ? 'contained' : 'outlined'}
                      >
                        Supplier
                      </Button>
                    </ButtonGroup>
                  }
                  label={undefined}
                />
              </FormGroup> */}
            {/* <ButtonGroup fullWidth variant="outlined">
                <Button onClick={() => setIsBuyer(true)} variant={isBuyer ? 'contained' : 'outlined'}>
                  Buyer
                </Button>
                <Button onClick={() => setIsBuyer(false)} variant={!isBuyer ? 'contained' : 'outlined'}>
                  Supplier
                </Button>
              </ButtonGroup> */}
            {/* <FormAutoCompleteByCountry
                id={'company'}
                getOptionKeyFn={(option) => `${option['name']}-${option['jurisdiction']}`}
                getOptionLabel={(option) => {
                  if (isCountryHaveState) {
                    return `${option['name']} - (${formatJurisdictionName(option['jurisdiction'] || '', option['country_code'] || '')})`;
                  }
                  return option['name'];
                }}
                countryCodeField={'country_code'}
                loading={dataCompanyResp.isFetching}
                required={true}
                countryCode={formik.values.company?.country_code || ''}
                inputValue={supplierSearchKeyword}
                placeholder={t('trade_data.search_form.input.keyword')}
                onBlur={() => {
                  formik.setFieldTouched('company');
                }}
                onTextInputChange={(value: string) => {
                  setSupplierSearchKeyword(value || '');
                }}
                onChange={(value: ITradeDataEntity) => {
                  setSupplierSearchKeyword(value?.name || '');
                }}
                options={dataCompanyResp.data?.data || []}
              />
              {renderFormItemError(getFormItemError('company', formik))} */}
            {/*</Stack>*/}
            <FormItemCountry label="Country" id="country_code" />
            <Stack direction={'row'} spacing={2}>
              <Box flex={1}>
                <DatePickerWithDefaultIcon
                  disableFuture={true}
                  value={formik.values.trade_from}
                  minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                  maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                  onChange={(data) => {
                    formik.setFieldValue('trade_from', data);
                    formik.setFieldTouched('trade_from');
                  }}
                  label={t('trade_data.search_form.input.trade_from')}
                  slotProps={{
                    textField: {
                      error: !!formik.errors.trade_from,
                      size: 'small',
                      fullWidth: true,
                      // required: true,
                    },
                  }}
                  views={['month', 'year']}
                />
                {renderError('trade_from')}
              </Box>
              <Box flex={1}>
                <DatePickerWithDefaultIcon
                  disableFuture={true}
                  value={formik.values.trade_to}
                  minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                  maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                  onChange={(data) => {
                    formik.setFieldValue('trade_to', data);
                    formik.setFieldTouched('trade_to');
                  }}
                  label={t('trade_data.search_form.input.trade_to')}
                  slotProps={{
                    textField: {
                      error: !!formik.errors.trade_from,
                      size: 'small',
                      fullWidth: true,
                      // required: true,
                    },
                  }}
                  views={['month', 'year']}
                />
                {renderError('trade_to')}
              </Box>
            </Stack>

            {transactionsCountResp.data?.data !== undefined && shouldExistAtLeastKeywordOrCompany && (
              <Stack
                flexDirection={'row'}
                gap={2}
                alignItems={'center'}
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  background: !isTransactionQueryPaid ? theme.palette.info.light : theme.palette.success.light,
                }}
              >
                {!isTransactionQueryPaid ? (
                  <>
                    <CreditCoinIcon sx={{ width: '22px', height: '22px' }} />
                    <Typography variant={'body2'}>
                      We've found{' '}
                      <Typography color={theme.palette.info.main} variant={'body2'} display={'inline'}>
                        {formatNumberWithSuffix(transactionsCountResp.data?.data)} Bills of Lading
                      </Typography>{' '}
                      matching your search criteria.
                    </Typography>
                  </>
                ) : (
                  <>
                    <>
                      <CheckedCredit sx={{ mb: 0.5 }} fontSize="medium" />
                      <Typography variant={'body2'}>
                        You have access to{' '}
                        <Typography color={theme.palette.success.main} variant={'body2'} display={'inline'}>
                          {formatNumberWithSuffix(transactionsCountResp.data?.data)} Bills of Lading
                        </Typography>{' '}
                        <Typography variant={'body2'} mr={0.5} display={'inline'}>
                          matching your search criteria.
                        </Typography>
                        <Tooltip title={'You have access to this data for 30 days'}>
                          <InfoOutlinedIcon
                            fontSize={'small'}
                            sx={{
                              color: theme.palette.text.secondary,
                              verticalAlign: 'middle',
                              mb: 0.5,
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>
                      </Typography>
                    </>
                  </>
                )}
              </Stack>
            )}
            <ButtonWithLoading
              sx={{ height: 53 }}
              size="large"
              variant="contained"
              isRounded
              isLoading={some([transactionsCountResp.isFetching, isPending])}
              type="submit"
              disabled={transactionsCountResp.data?.data === 0}
              startIcon={<TravelExplore />}
            >
              {!isTransactionQueryPaid ? (
                <>
                  <CreditCostIndicator pointData={pointsViewTradeData} />
                  <Typography ml={2} color={'white'}>
                    Unlock Trade Data Now
                  </Typography>
                  <ArrowForwardIcon sx={{ ml: 1 }} fontSize={'small'} />
                </>
              ) : (
                <>
                  <CheckedCredit fontSize="medium" />
                  <Typography ml={2} color={'white'}>
                    Access Trade Data Now
                  </Typography>
                  <ArrowForwardIcon sx={{ ml: 1 }} fontSize={'small'} />
                </>
              )}
            </ButtonWithLoading>
            {transactionsCountResp.isFetching && (
              <Typography align="center" variant="caption2" color="text.secondary">
                Please wait a few seconds while we compile your report.
              </Typography>
            )}
          </Stack>
        </Box>
      </FormikForm>
    </Box>
  );
};

export default FormSearchCompanyTrade;
